import { useQuery } from '@apollo/client';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';

import { TaxForm, TaxFormType } from '@bybeam/platform-types';
import Loading from '@platform-ui-common/components/Loading/index.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar/index.js';
import GetUserTaxForms from '../graphql/queries/GetUserTaxForms.graphql';

export interface TaxFormsState {
  taxForms: TaxForm[];
  taxFormWasSubmitted: (type: TaxFormType) => boolean;
}

export function checkIfTaxFormWasSubmitted(taxForms: TaxForm[], type: TaxFormType) {
  return !!taxForms?.find((taxForm: TaxForm) => taxForm.type === type);
}

// Only exported for use in tests/mocks; do NOT reference directly in prod code
export const TaxFormsContext = React.createContext<TaxFormsState>({} as TaxFormsState);

export function TaxFormsProvider({ children }: { children: ReactNode }): JSX.Element {
  const { showSnackbar } = useSnackbar();
  const { loading, data, error } = useQuery<{
    currentUser: { id: string; taxForms: TaxForm[] };
  }>(GetUserTaxForms);

  useEffect(() => {
    if (error) showSnackbar('Something went wrong! Please refresh the page');
  }, [error, showSnackbar]);

  const taxForms = useMemo(() => {
    if (loading || error || !data) return [];
    return data?.currentUser?.taxForms;
  }, [loading, error, data]);

  // it is assumed that taxForms are submitted once per type.
  // if that were to change, this function should be updated
  const taxFormWasSubmitted = useCallback(
    (type: TaxFormType): boolean => checkIfTaxFormWasSubmitted(data?.currentUser?.taxForms, type),
    [data],
  );

  return (
    <TaxFormsContext.Provider value={{ taxForms, taxFormWasSubmitted }}>
      {loading ? <Loading size="XXL" fullPage /> : children}
    </TaxFormsContext.Provider>
  );
}

export default function useTaxForms(): TaxFormsState {
  return useContext(TaxFormsContext);
}
